import { useState } from "react";
import { Link } from "@remix-run/react";
import type { MetaFunction } from "@remix-run/node";
import { calculateCycle, validateCycleData, getCurrentCycleStatus } from "~/utils/cycleCalculations";
import CycleVisualizer from "~/components/CycleVisualizer";
import { SEOStructuredData, Breadcrumb } from "~/components/SEOStructuredData";

export const meta: MetaFunction = () => {
  return [
    { title: "月经周期管理 - 排卵期计算 - 安全期预测 | 女孩的秘密" },
    { 
      name: "description", 
      content: "女孩的秘密月经周期管理工具，科学计算排卵期、安全期和易孕期。基于医学算法，准确预测生理周期，帮助女性更好地了解身体变化，制定健康计划。支持周期记录、症状追踪。" 
    },
    { 
      name: "keywords", 
      content: "月经周期管理,排卵期计算,安全期计算,易孕期预测,生理周期追踪,月经记录,排卵日计算,女性健康管理,生理期管理,月经周期表" 
    },
    { name: "robots", content: "index, follow" },
    { name: "googlebot", content: "index, follow" },
    { name: "bingbot", content: "index, follow" },
    { name: "author", content: "Girls Secret Team" },
    { name: "language", content: "zh-CN" },
    
    // Open Graph
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://girls-secret.com/cycle" },
    { property: "og:title", content: "月经周期管理 - 排卵期计算 - 女孩的秘密" },
    { property: "og:description", content: "科学管理月经周期，准确计算排卵期、安全期和易孕期，帮助女性更好地了解身体周期变化。" },
    { property: "og:image", content: "https://girls-secret.com/og-image-cycle.jpg" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:locale", content: "zh_CN" },
    { property: "og:site_name", content: "女孩的秘密" },
    
    // Twitter
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "月经周期管理 - 排卵期计算 - 女孩的秘密" },
    { name: "twitter:description", content: "科学管理月经周期，准确计算排卵期、安全期和易孕期，帮助女性更好地了解身体周期变化。" },
    { name: "twitter:image", content: "https://girls-secret.com/og-image-cycle.jpg" },
    
    // 移动端优化
    { name: "mobile-web-app-capable", content: "yes" },
    { name: "apple-mobile-web-app-capable", content: "yes" },
    { name: "format-detection", content: "telephone=no" },
  ];
};

interface CycleData {
  lastPeriodDate: string;
  cycleLength: number;
  periodLength: number;
}

interface CycleResult {
  nextPeriod: Date;
  ovulationStart: Date;
  ovulationEnd: Date;
  fertileStart: Date;
  fertileEnd: Date;
  safeBeforeStart: Date;
  safeBeforeEnd: Date;
  safeAfterStart: Date;
  safeAfterEnd: Date;
}

export default function Cycle() {
  const [formData, setFormData] = useState<CycleData>({
    lastPeriodDate: '',
    cycleLength: 28,
    periodLength: 5
  });
  const [result, setResult] = useState<CycleResult | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  
  const breadcrumbs = [
    { name: "首页", url: "/" },
    { name: "月经周期管理", url: "/cycle", current: true }
  ];

  const handleInputChange = (field: keyof CycleData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // 清除错误信息
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleCalculate = () => {
    setIsCalculating(true);
    
    // 验证输入数据
    const validation = validateCycleData(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      setIsCalculating(false);
      return;
    }

    // 计算周期
    try {
      const calculationResult = calculateCycle(formData);
      setResult(calculationResult);
      setErrors([]);
    } catch (error) {
      setErrors(['计算过程中出现错误，请检查输入数据']);
    }
    
    setIsCalculating(false);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysUntil = (date: Date) => {
    const today = new Date();
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <SEOStructuredData type="cycle" breadcrumbs={breadcrumbs} />
      {/* Header */}
      <header className="glass-card mx-4 mt-4 p-6">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link to="/" className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">💕</span>
            </Link>
            <h1 className="text-2xl font-bold gradient-text">月经周期管理</h1>
          </div>
          <nav className="hidden md:flex space-x-6">
            <Link to="/" className="text-gray-600 hover:text-pink-600 transition-colors">
              首页
            </Link>
            <Link to="/cycle" className="text-pink-600 font-medium">
              周期管理
            </Link>
            <Link to="/pregnancy" className="text-gray-600 hover:text-pink-600 transition-colors">
              预产期计算
            </Link>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <Breadcrumb items={breadcrumbs} />
        <div className="grid lg:grid-cols-2 gap-8">
          {/* 输入表单 */}
          <div className="glass-card p-8">
            <h2 className="text-2xl font-bold gradient-text mb-6">输入周期信息</h2>
            <p className="text-gray-600 mb-6">输入您的月经周期信息，我们将为您计算排卵期、安全期和易孕期</p>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  末次月经开始日期
                </label>
                <input
                  type="date"
                  value={formData.lastPeriodDate}
                  onChange={(e) => handleInputChange('lastPeriodDate', e.target.value)}
                  className="input-field"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  月经周期长度（天）
                </label>
                <input
                  type="number"
                  value={formData.cycleLength}
                  onChange={(e) => handleInputChange('cycleLength', parseInt(e.target.value) || 28)}
                  className="input-field"
                  min="21"
                  max="35"
                  placeholder="通常为21-35天"
                />
                <p className="text-xs text-gray-500 mt-1">从月经第一天到下次月经第一天的天数</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  月经持续时间（天）
                </label>
                <input
                  type="number"
                  value={formData.periodLength}
                  onChange={(e) => handleInputChange('periodLength', parseInt(e.target.value) || 5)}
                  className="input-field"
                  min="3"
                  max="7"
                  placeholder="通常为3-7天"
                />
                <p className="text-xs text-gray-500 mt-1">月经持续的天数</p>
              </div>

              {errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="text-red-800 font-medium mb-2">输入错误：</h4>
                  <ul className="text-red-700 text-sm space-y-1">
                    {errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={handleCalculate}
                disabled={isCalculating}
                className={`w-full btn-primary ${isCalculating ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isCalculating ? (
                  <span className="flex items-center justify-center">
                    <div className="loading-pulse mr-2">⏳</div>
                    计算中...
                  </span>
                ) : (
                  '计算周期'
                )}
              </button>
            </div>
          </div>

          {/* 计算结果 */}
          <div className="space-y-6">
            {result && (
              <>
                {/* 周期可视化 */}
                <div className="glass-card p-8">
                  <h3 className="text-xl font-bold gradient-text mb-6 text-center">周期可视化</h3>
                  <CycleVisualizer result={result} />
                  <div className="mt-6 text-center text-sm text-gray-600">
                    {getCurrentCycleStatus(result, new Date()).description}
                  </div>
                </div>

                {/* 详细结果 */}
                <div className="glass-card p-8 data-load">
                  <h3 className="text-xl font-bold gradient-text mb-6">计算结果</h3>
                  
                  <div className="space-y-4">
                    <div className="bg-pink-50 rounded-lg p-4">
                      <h4 className="font-semibold text-pink-800 mb-2">📅 下次月经期</h4>
                      <p className="text-pink-700">
                        {formatDate(result.nextPeriod)}
                        <span className="text-sm ml-2">
                          ({getDaysUntil(result.nextPeriod) > 0 ? `还有 ${getDaysUntil(result.nextPeriod)} 天` : '已过期'})
                        </span>
                      </p>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4">
                      <h4 className="font-semibold text-purple-800 mb-2">🥚 排卵期</h4>
                      <p className="text-purple-700">
                        {formatDate(result.ovulationStart)} - {formatDate(result.ovulationEnd)}
                      </p>
                      <p className="text-sm text-purple-600 mt-1">
                        排卵日通常在 {formatDate(new Date((result.ovulationStart.getTime() + result.ovulationEnd.getTime()) / 2))}
                      </p>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="font-semibold text-green-800 mb-2">💚 易孕期</h4>
                      <p className="text-green-700">
                        {formatDate(result.fertileStart)} - {formatDate(result.fertileEnd)}
                      </p>
                      <p className="text-sm text-green-600 mt-1">
                        包括排卵期前后的高受孕概率时期
                      </p>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-800 mb-2">🛡️ 安全期</h4>
                      <div className="space-y-2">
                        <p className="text-blue-700">
                          <span className="font-medium">月经前安全期：</span>
                          {formatDate(result.safeBeforeStart)} - {formatDate(result.safeBeforeEnd)}
                        </p>
                        <p className="text-blue-700">
                          <span className="font-medium">月经后安全期：</span>
                          {formatDate(result.safeAfterStart)} - {formatDate(result.safeAfterEnd)}
                        </p>
                      </div>
                      <p className="text-sm text-blue-600 mt-2">
                        安全期仅供参考，不能作为避孕的唯一方法
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* 注意事项 */}
            <div className="glass-card p-6">
              <h4 className="font-semibold text-gray-800 mb-3">⚠️ 重要提醒</h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• 计算结果基于平均周期，个体差异可能影响准确性</li>
                <li>• 压力、疾病、药物等因素可能影响月经周期</li>
                <li>• 安全期避孕法成功率较低，建议配合其他避孕方法</li>
                <li>• 如有月经不调等问题，请及时咨询医生</li>
                <li>• 本工具仅供参考，不能替代专业医疗建议</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}